import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  ArrowLeft, Anchor, TrendingUp, TrendingDown, Users, Calendar,
  Award, FileText, BarChart3, Sparkles, Search, Inbox, Quote
} from 'lucide-react';

// Aperçu complet du thème "Dossier Bancaire" - réservé aux admins
const ThemePreviewPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [loadingOpen, setLoadingOpen] = useState(false);

  if (!user || user.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-paper">
        <p className="text-ink-soft">Accès réservé aux admins.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper text-ink" data-testid="theme-preview-page">
      {/* Header */}
      <header className="border-b border-line bg-white">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/')} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-3">
            <Anchor className="w-7 h-7 text-gold" />
            <div>
              <h1 className="text-2xl m-0 filet-gold">Cabinet Odys — Aperçu Thématique</h1>
              <p className="text-sm italic text-muted-dossier mt-3 mb-0">
                Identité visuelle "Dossier Bancaire" — démonstration des composants.
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10 space-y-12">

        {/* 1. Titres & Filets */}
        <section data-testid="section-typography">
          <p className="dossier-label">§ 01 — Typographie</p>
          <h2 className="text-3xl mb-2 filet-gold">Titres en serif Georgia</h2>
          <p className="text-ink-soft max-w-2xl">
            Le système typographique repose sur Georgia pour les titres et les chiffres,
            Inter pour les labels uppercase et les boutons. Chaque titre est souligné
            d'un filet or dégradé, comme le tampon d'un cabinet bancaire suisse.
          </p>
          <div className="mt-6 grid md:grid-cols-3 gap-6">
            <div>
              <h3 className="filet-gold">H3 Section</h3>
              <p className="text-sm text-muted-dossier">Sous-titre de section avec filet or.</p>
            </div>
            <div>
              <h4 className="filet-gold">H4 Sous-section</h4>
              <p className="text-sm text-muted-dossier">Hiérarchie soutenue par le serif.</p>
            </div>
            <div>
              <p className="dossier-label">Label uppercase</p>
              <p className="metric-info">Information italique secondaire</p>
            </div>
          </div>
        </section>

        {/* 2. Cards KPI Thématiques */}
        <section data-testid="section-cards">
          <p className="dossier-label">§ 02 — Cards KPI</p>
          <h2 className="text-3xl mb-6 filet-gold">Cartes patrimoniales</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="dossier-card is-premium" data-testid="card-premium">
              <p className="dossier-label">Patrimoine total</p>
              <div className="flex items-center gap-3 mt-3">
                <div className="icon-circle is-gold">
                  <Sparkles className="w-6 h-6" />
                </div>
                <div>
                  <div className="metric-number">1 247 €</div>
                  <span className="dossier-pill is-gold mt-2">+24,5%</span>
                </div>
              </div>
              <p className="metric-info">Patrimoine sous gestion mensuel</p>
            </div>

            <div className="dossier-card is-positive" data-testid="card-positive">
              <p className="dossier-label">Croissance XP</p>
              <div className="flex items-center gap-3 mt-3">
                <div className="icon-circle is-teal">
                  <TrendingUp className="w-6 h-6" />
                </div>
                <div>
                  <div className="metric-number">8 320</div>
                  <span className="dossier-pill is-teal mt-2">Niveau 12</span>
                </div>
              </div>
              <p className="metric-info">Capitaine de Frégate</p>
            </div>

            <div className="dossier-card is-neutral" data-testid="card-neutral">
              <p className="dossier-label">Équipage actif</p>
              <div className="flex items-center gap-3 mt-3">
                <div className="icon-circle">
                  <Users className="w-6 h-6" />
                </div>
                <div>
                  <div className="metric-number">142</div>
                  <span className="dossier-pill is-ink mt-2">+12 cette semaine</span>
                </div>
              </div>
              <p className="metric-info">Membres connectés ces 7 derniers jours</p>
            </div>

            <div className="dossier-card is-danger" data-testid="card-danger">
              <p className="dossier-label">Streak rompu</p>
              <div className="flex items-center gap-3 mt-3">
                <div className="icon-circle is-danger">
                  <TrendingDown className="w-6 h-6" />
                </div>
                <div>
                  <div className="metric-number">-3</div>
                  <span className="dossier-pill is-danger mt-2">À reconstruire</span>
                </div>
              </div>
              <p className="metric-info">Jours d'absence consécutifs</p>
            </div>
          </div>
        </section>

        {/* 3. Boutons */}
        <section data-testid="section-buttons">
          <p className="dossier-label">§ 03 — Boutons en pills</p>
          <h2 className="text-3xl mb-6 filet-gold">Actions</h2>
          <div className="flex flex-wrap gap-3">
            <button className="btn-pill-ink" data-testid="btn-ink">
              <Anchor className="w-4 h-4" /> Action principale
            </button>
            <button className="btn-pill-gold" data-testid="btn-gold">
              <Sparkles className="w-4 h-4" /> Action premium
            </button>
            <button className="btn-pill-teal" data-testid="btn-teal">
              <TrendingUp className="w-4 h-4" /> Action positive
            </button>
            <button className="btn-pill-secondary" data-testid="btn-secondary">
              <FileText className="w-4 h-4" /> Action secondaire
            </button>
            <button className="btn-pill-danger" data-testid="btn-danger">
              <TrendingDown className="w-4 h-4" /> Action risquée
            </button>
          </div>
        </section>

        {/* 4. Inputs */}
        <section data-testid="section-inputs">
          <p className="dossier-label">§ 04 — Champs</p>
          <h2 className="text-3xl mb-6 filet-gold">Saisie</h2>
          <div className="dossier-card max-w-xl space-y-4">
            <div>
              <label className="dossier-label">Nom du destinataire</label>
              <Input placeholder="ex: Alexandre Dupont" className="mt-1" />
            </div>
            <div>
              <label className="dossier-label">Adresse de livraison</label>
              <Textarea placeholder="Rue, ville, code postal" rows={3} className="mt-1" />
            </div>
            <button className="btn-pill-ink w-full">Soumettre la demande</button>
          </div>
        </section>

        {/* 5. Modal */}
        <section data-testid="section-modal">
          <p className="dossier-label">§ 05 — Modale "Fiche d'instruction"</p>
          <h2 className="text-3xl mb-6 filet-gold">Dialogue</h2>
          <button className="btn-pill-gold" onClick={() => setModalOpen(true)} data-testid="open-modal-btn">
            <FileText className="w-4 h-4" /> Ouvrir la fiche
          </button>
          <Dialog open={modalOpen} onOpenChange={setModalOpen}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="filet-gold">Fiche d'instruction n°1247</DialogTitle>
                <DialogDescription className="italic">
                  Validation d'opération patrimoniale — confirmation requise.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 py-4">
                <div className="flex justify-between border-b border-line pb-2">
                  <span className="dossier-label m-0">Montant</span>
                  <span className="font-serif font-bold">12 500 €</span>
                </div>
                <div className="flex justify-between border-b border-line pb-2">
                  <span className="dossier-label m-0">Bénéficiaire</span>
                  <span>Cabinet Odys SARL</span>
                </div>
                <div className="flex justify-between">
                  <span className="dossier-label m-0">Date</span>
                  <span>{new Date().toLocaleDateString('fr-FR')}</span>
                </div>
              </div>
              <DialogFooter>
                <button className="btn-pill-secondary" onClick={() => setModalOpen(false)}>Annuler</button>
                <button className="btn-pill-ink" onClick={() => setModalOpen(false)}>Confirmer</button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </section>

        {/* 6. Empty State */}
        <section data-testid="section-empty">
          <p className="dossier-label">§ 06 — État vide</p>
          <h2 className="text-3xl mb-6 filet-gold">Empty state</h2>
          <div className="empty-state-dossier">
            <div className="icon-circle">
              <Inbox className="w-9 h-9" />
            </div>
            <h3>Aucune victoire enregistrée</h3>
            <p>
              Votre carnet patrimonial est encore vierge. Partagez votre première
              réussite immobilière pour inaugurer ce dossier.
            </p>
            <button className="btn-pill-ink">
              <Award className="w-4 h-4" /> Inscrire une victoire
            </button>
          </div>
        </section>

        {/* 7. Pills & Badges */}
        <section data-testid="section-pills">
          <p className="dossier-label">§ 07 — Pills sémantiques</p>
          <h2 className="text-3xl mb-6 filet-gold">Indicateurs</h2>
          <div className="flex flex-wrap gap-3">
            <span className="dossier-pill is-gold">Premium</span>
            <span className="dossier-pill is-teal">Positif</span>
            <span className="dossier-pill is-ink">Confirmé</span>
            <span className="dossier-pill is-danger">À traiter</span>
            <span className="level-badge">Niveau 12 — Capitaine</span>
            <span className="tag">#immobilier</span>
            <span className="tag">#fiscalité</span>
          </div>
        </section>

        {/* 8. Loading screen demo */}
        <section data-testid="section-loading">
          <p className="dossier-label">§ 08 — Écran de chargement</p>
          <h2 className="text-3xl mb-6 filet-gold">Chargement premium</h2>
          <button className="btn-pill-gold" onClick={() => {
            setLoadingOpen(true);
            setTimeout(() => setLoadingOpen(false), 3500);
          }} data-testid="open-loading-btn">
            <BarChart3 className="w-4 h-4" /> Démo loader (3,5s)
          </button>
        </section>

        <footer className="pt-10 border-t border-line text-center">
          <p className="italic text-muted-dossier text-sm">
            Cabinet Odys — Aperçu typographique et chromatique. Réservé à l'usage interne.
          </p>
        </footer>
      </main>

      {/* Loading screen overlay */}
      {loadingOpen && (
        <div className="dossier-loading-screen" data-testid="loading-screen">
          <div className="dossier-loading-ornament">
            <svg className="star" viewBox="0 0 100 100" fill="none" stroke="hsl(var(--gold))" strokeWidth="1.5">
              <circle cx="50" cy="50" r="48" strokeDasharray="2 4" />
              <circle cx="50" cy="50" r="38" strokeDasharray="2 3" />
              <path d="M50 12 L54 46 L88 50 L54 54 L50 88 L46 54 L12 50 L46 46 Z" fill="hsl(var(--gold) / 0.15)" stroke="hsl(var(--gold-dark))" strokeWidth="2" />
            </svg>
            <div className="ring" />
          </div>
          <h2 className="dossier-loading-title">L'Équipage Odys</h2>
          <p className="dossier-loading-quote">
            « L'investissement le plus important que vous puissiez faire est en vous-même. »
          </p>
          <p className="dossier-loading-author">— Warren Buffett</p>
        </div>
      )}
    </div>
  );
};

export default ThemePreviewPage;
